import type { ReactNode } from 'react';
import React from 'react';
import type { FormikProps } from 'formik';
import { FieldArray, FormikProvider } from 'formik';
import type { ButtonV2Props, FlexProps } from '@withjoy/joykit';
import { Flex, ButtonV2, SpacingStack, Divider, TextV2, Box } from '@withjoy/joykit';
import { useId } from '@withjoy/joykit/hooks';
import type { Contacts, Context, IframeContacts, FieldVisibility } from '../../shared/types';
import { useFormController } from './Form.controller';
import { Wrapper, Footer } from '../components';
import { AddMember, ContactInfo, Header, MailingAddress, Tabs, TermsAndConditions } from './components';
import { RegistrationContactFields } from '@shared/components/EventForm/components/presentational/steps/ContactStep/RegistrationContactFields';
import { RegistrationMailingAddress } from '@shared/components/EventForm/components/presentational/steps/ContactStep/RegistrationMailingAddress';
import { RegistrationConsent } from '@shared/components/EventForm/components/presentational/steps/ContactStep/RegistrationConsent';
import { useNameAffixSuggestions } from '@shared/components/NameAffixField/useNameAffixSuggestions';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import type { RoutesContextProps } from '@shared/core/router.helper/RoutesContext';
import { FieldErrorAnnouncementProvider, announceFieldError } from '@shared/joykit/packages/core/components/FormControl';

interface FormProps
  extends Readonly<{
    formik: FormikProps<Contacts> | FormikProps<IframeContacts>;
    context?: Context;
    isCollectHouseholdEnabled: boolean;
    isIframeVersion?: boolean;
    phoneNumberRule?: FieldVisibility;
    mailingAddressRule?: FieldVisibility;
    emailRule?: FieldVisibility;
    isRegistration?: boolean;
    routeHelpers?: RoutesContextProps;
    isMutationLoading?: boolean;
    hasMutationError?: boolean;
    e164PhoneNumber: { [key: number]: string };
    setE164PhoneNumber: React.Dispatch<React.SetStateAction<{ [key: number]: string }>>;
    hideTermsAndConditions?: boolean;
    formatIntlTelInputOnInit?: boolean;
    isMobileGuesList?: boolean;
    overrides?: {
      submitButtonProps?: ButtonV2Props;
      submitButtonLabel?: string;
      extraCTAs?: ReactNode;
      /** The row holding `extraCTAs` and the submit button. */
      buttonRowProps?: FlexProps;
      beforeButtons?: ReactNode;
      wrapperProps?: FlexProps;
      disableAddressValidation?: boolean;
      formMinHeight?: string;
      formHeader?: ReactNode;
      /**
       * Wraps the row holding the submit button, so a caller can render it somewhere else (the
       * registration page's action bar). The button stays tied to the form through its
       * `form` attribute, so Enter in a field still submits.
       */
      wrapButtonRow?: (row: ReactNode) => ReactNode;
    };
    isMailingAddressOpenedByDefault?: boolean;
    /**
     * When set, renders the optional Title (prefix) / Suffix inputs with
     * locale-keyed suggestions for this event. Off by default so the other
     * reuses of this form (registration, website-embedded forms, admin guest
     * list) stay unchanged.
     */
    nameAffixesEventId?: string | null;
    /**
     * Capability token authorizing the suggestions query on the public guest
     * collector (no session there). Admin surfaces omit it — the logged-in
     * event admin authorizes the query instead.
     */
    nameAffixesContactCollectionId?: string | null;
    isUpdateMode?: boolean;
    phoneInputResetKey?: number;
    showEnvelopeNameOverrideField?: boolean;
    phoneCountryCode?: string;
    // Optional caller-owned navigation for "advance to address validation",
    // used when Form is rendered outside a routing context (e.g. in a dialog).
    onNavigateToAddress?: () => void;
  }> {}

export const Form: React.FC<FormProps> = ({
  formik,
  context,
  isCollectHouseholdEnabled,
  isIframeVersion = false,
  phoneNumberRule = 'optional',
  mailingAddressRule = 'optional',
  emailRule = 'required',
  isRegistration = false,
  routeHelpers,
  isMutationLoading,
  hasMutationError,
  e164PhoneNumber,
  setE164PhoneNumber,
  hideTermsAndConditions = false,
  // Retained for caller API compatibility; no longer forwarded now that
  // IntlTelInputWrapper handles formatting on init itself.
  formatIntlTelInputOnInit: _formatIntlTelInputOnInit = true,
  isMobileGuesList = false,
  overrides,
  isMailingAddressOpenedByDefault = false,
  nameAffixesEventId,
  nameAffixesContactCollectionId,
  isUpdateMode = false,
  phoneInputResetKey,
  showEnvelopeNameOverrideField = false,
  onNavigateToAddress,
  phoneCountryCode = 'us',
}) => {
  // Ties the submit button to the form even when a caller renders the button row elsewhere.
  const formId = useId(undefined, 'contact-form');
  const {
    tAdmin,
    handleOnSaveDetailsClick,
    errors: { firstNameError, lastNameError, emailError, address1Error, postalCodeError, cityError, regionError, countryError },
    handleOnCountryDropdownChange,
    isEmailInvalid,
    isPhoneNumberInvalidWithoutFocus,
    setIsPhoneNumberValid,
    phoneNumberTouched,
    setPhoneNumberTouched,
  } = useFormController(
    formik,
    isIframeVersion,
    e164PhoneNumber,
    setE164PhoneNumber,
    context?.coupleNames,
    routeHelpers,
    phoneNumberRule,
    mailingAddressRule,
    emailRule,
    overrides?.disableAddressValidation,
    onNavigateToAddress,
  );

  const nameAffixSuggestions = useNameAffixSuggestions(nameAffixesEventId, nameAffixesContactCollectionId ?? undefined);

  const hasValidPhoneNumber = formik.values.contacts.some((contact, index) => !!contact.phoneNumber && !isPhoneNumberInvalidWithoutFocus(index));
  const showSmsTerms = !hideTermsAndConditions && phoneNumberRule !== 'hidden' && (phoneNumberRule === 'required' || hasValidPhoneNumber);
  const buttonLabel = overrides?.submitButtonLabel ?? tAdmin.saveDetails();

  // Registration: when Next finds something missing, focus moves to the first field with an error,
  // once the errors have rendered, and reads its error. The page's scroll padding keeps it clear of
  // the pinned bar. Next keeps focus in the field the guest was typing in (so the button doesn't move
  // under the pointer); when that is the first invalid field, focus doesn't move and nothing would
  // read its new error, so it is announced.
  const handleRegistrationSubmitClick = async () => {
    await handleOnSaveDetailsClick();
    requestAnimationFrame(() => {
      const form = document.getElementById(formId);
      const firstInvalid = form?.querySelector<HTMLElement>('[aria-invalid="true"]');
      if (!firstInvalid) return;
      if (firstInvalid === document.activeElement) announceFieldError(firstInvalid);
      else firstInvalid.focus();
    });
  };

  if (isMutationLoading && !isMobileGuesList) return <JoyLogoLoader loaderKey="contact-collector-form" />;

  return (
    <Wrapper
      wrapperOverrides={{
        // In the iframe (admin guest list), an ancestor can stretch the wrapper to a
        // viewport-derived height that Safari intermittently resolves short while the host
        // modal is mid-animation. Vertical centering (alignItems: center) then pushes the top
        // of a taller form out of view — the Safari-only "clipped/sliver" guest-detail bug.
        // Top-align so any overflow goes downward (visible and scrollable) instead of upward.
        ...(isIframeVersion && { alignItems: 'flex-start' }),
        ...overrides?.wrapperProps,
      }}
    >
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        // Registration sits inside the event form, which sets the side margin for every step.
        paddingX={isRegistration ? 0 : [isIframeVersion ? '40px' : '24px', null, 0]}
        width="100%"
        marginBottom={isIframeVersion && !isRegistration ? '40px' : 0}
      >
        {!isIframeVersion && (
          <Header
            title={tAdmin.addYourInfo()}
            coupleNames={context?.coupleNames}
          />
        )}
        <FormikProvider
          key="contact-collector"
          value={formik}
        >
          {/* Registration reads out a field's error when the guest leaves the field with it. */}
          <FieldErrorAnnouncementProvider value={isRegistration}>
            <form
              id={formId}
              // Registration shows its own errors under each field, in the event's language; the
              // browser's "Please fill out this field" bubble would repeat them in another.
              noValidate={isRegistration}
              style={{ width: '100%' }}
              onSubmit={e => {
                // prevent default form submit navigation behavior
                e.preventDefault();
              }}
            >
              <Box minHeight={overrides?.formMinHeight}>
                {overrides?.formHeader}
                <FieldArray name="contacts">
                  {({ push, remove }) => (
                    <>
                      {isCollectHouseholdEnabled && (
                        <Tabs
                          formik={formik}
                          push={push}
                          isIframeVersion={isIframeVersion}
                        />
                      )}
                      {/* Registration asks for one guest, with its own fields, address card and copy.
                        The contact collector and the admin forms keep theirs. */}
                      {isRegistration && (
                        <>
                          <RegistrationContactFields
                            formik={formik}
                            firstNameError={firstNameError(0)}
                            lastNameError={lastNameError(0)}
                            emailError={emailError(0)}
                            phoneNumberRule={phoneNumberRule}
                            // Like every other field, the phone shows a problem once the guest has left it
                            // or pressed Next, not on the first digit they type.
                            isPhoneNumberInvalid={!!phoneNumberTouched[0] && isPhoneNumberInvalidWithoutFocus(0)}
                            setIsPhoneNumberValid={isValid => setIsPhoneNumberValid(previous => ({ ...previous, 0: isValid }))}
                            onPhoneBlur={() => setPhoneNumberTouched(previous => ({ ...previous, 0: true }))}
                            phoneInputResetKey={phoneInputResetKey}
                            phoneCountryCode={phoneCountryCode}
                          />
                          {mailingAddressRule !== 'hidden' && (
                            <RegistrationMailingAddress
                              formik={formik}
                              mailingAddressRule={mailingAddressRule}
                              onCountryChange={handleOnCountryDropdownChange}
                              errors={{ address1: address1Error(0), city: cityError(0), region: regionError(0), postalCode: postalCodeError(0), country: countryError(0) }}
                            />
                          )}
                        </>
                      )}
                      {!isRegistration &&
                        formik.values.contacts.map((contact, index) => (
                          <SpacingStack
                            key={index}
                            spacing={6}
                          >
                            <ContactInfo
                              formik={formik}
                              index={index}
                              errors={{ firstNameError, lastNameError, emailError }}
                              isEmailInvalid={isEmailInvalid}
                              remove={remove}
                              isIframeVersion={isIframeVersion}
                              phoneNumberRule={phoneNumberRule}
                              emailRule={emailRule}
                              isRegistration={isRegistration}
                              showNameAffixFields={!!nameAffixesEventId}
                              nameAffixSuggestions={nameAffixSuggestions}
                              isPhoneNumberInvalidWithoutFocus={isPhoneNumberInvalidWithoutFocus}
                              setIsPhoneNumberValid={setIsPhoneNumberValid}
                              phoneNumberTouched={phoneNumberTouched}
                              setPhoneNumberTouched={setPhoneNumberTouched}
                              phoneInputResetKey={phoneInputResetKey}
                              phoneCountryCode={phoneCountryCode}
                            />
                            {index === 0 && mailingAddressRule !== 'hidden' && (
                              <MailingAddress
                                showEnvelopeNameOverrideField={showEnvelopeNameOverrideField}
                                formik={formik}
                                index={index}
                                contact={contact}
                                handleOnCountryDropdownChange={handleOnCountryDropdownChange}
                                errors={{ address1Error, postalCodeError, cityError, regionError, countryError }}
                                isIframeVersion={isIframeVersion}
                                mailingAddressRule={mailingAddressRule}
                                isSectionOpenedByDefault={isMailingAddressOpenedByDefault}
                                isUpdateMode={isUpdateMode}
                              />
                            )}
                          </SpacingStack>
                        ))}
                      {isCollectHouseholdEnabled && (
                        <>
                          <Divider marginTop="2rem" />
                          <AddMember
                            push={push}
                            title={tAdmin.addMember()}
                            isIframeVersion={isIframeVersion}
                          />
                        </>
                      )}
                    </>
                  )}
                </FieldArray>
                {isIframeVersion && !isRegistration && (
                  <TextV2
                    typographyVariant="label1"
                    fontWeight={800}
                    marginTop="16px"
                  >
                    {tAdmin.requiredFields()}
                  </TextV2>
                )}
              </Box>
              {overrides?.beforeButtons}
              {(overrides?.wrapButtonRow ?? (buttonRow => buttonRow))(
                <Flex
                  marginTop="1.5rem"
                  width="100%"
                  columnGap="6"
                  {...overrides?.buttonRowProps}
                >
                  {overrides?.extraCTAs}
                  <ButtonV2
                    intent="primary"
                    shape="rounded"
                    onClick={isRegistration ? handleRegistrationSubmitClick : handleOnSaveDetailsClick}
                    type="submit"
                    form={formId}
                    fullWidth
                    disabled={isMutationLoading && isMobileGuesList}
                    {...overrides?.submitButtonProps}
                  >
                    {buttonLabel}
                  </ButtonV2>
                </Flex>,
              )}
              {showSmsTerms && (isRegistration ? <RegistrationConsent /> : <TermsAndConditions buttonLabel={buttonLabel} />)}
            </form>
          </FieldErrorAnnouncementProvider>
        </FormikProvider>
        {hasMutationError && (
          <TextV2
            typographyVariant="body3"
            color="red"
            marginTop="16px"
          >
            {tAdmin.contactError()}
          </TextV2>
        )}
        {!isIframeVersion && <Footer />}
      </Flex>
    </Wrapper>
  );
};
